import React from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useGetCategoriesQuery } from "../../api/productTaxonomyApi";
import { TransformedProduct } from "../../types/productApiTypes";
import { TransformedWPCategory } from "../../types/productTaxonomyApiTypes";

interface BreadcrumbsProps {
  product: TransformedProduct | null | undefined;
  isProductLoading: boolean;
}

interface BreadcrumbItem {
  name: string;
  link?: string;
}

const BreadcrumbsSkeleton: React.FC = () => (
  <nav className="product-single__breadcrumbs" aria-label="Breadcrumb">
    <Skeleton height={20} width={300} />
  </nav>
);

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  product,
  isProductLoading,
}) => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const { data: categoriesData, isLoading: areCategoriesLoading } =
    useGetCategoriesQuery();

  const isComponentLoading =
    isProductLoading || areCategoriesLoading || !areTranslationsReady;

  if (isComponentLoading) {
    return <BreadcrumbsSkeleton />;
  }

  if (!product) {
    return null; // Don't render if there's no product data
  }

  // Build the breadcrumb path
  const breadcrumbItems: BreadcrumbItem[] = [];

  // 1. Home
  breadcrumbItems.push({
    name: t("productSingle.breadcrumbs.home", "Home"),
    link: window.commonVariables?.siteUrl || "/",
  });

  // 2. Shop
  breadcrumbItems.push({
    name: t("productSingle.breadcrumbs.shop", "Shop"),
    link: window.commonVariables?.shopUrl || "/shop",
  });

  // 3. Category Path
  const primaryCategory = product.enrichedCategories?.[0];
  if (primaryCategory && categoriesData?.categories) {
    const allCategories = categoriesData.categories;
    const categoryPath: TransformedWPCategory[] = [];
    let currentCategory: TransformedWPCategory | undefined =
      allCategories[primaryCategory.id];

    // Traverse up the parent tree
    while (currentCategory) {
      categoryPath.unshift(currentCategory);
      if (currentCategory.parent === 0) break;
      currentCategory = allCategories[currentCategory.parent];
    }

    categoryPath.forEach((cat) => {
      breadcrumbItems.push({
        name: cat.name,
        link: cat.link, // Use the link property from the category object
      });
    });
  }

  // 4. Current Product (not a link)
  breadcrumbItems.push({ name: product.name });

  return (
    <nav className="product-single__breadcrumbs" aria-label="Breadcrumb">
      <ol>
        {breadcrumbItems.map((item, index) => (
          <li key={index}>
            {item.link && index < breadcrumbItems.length - 1 ? (
              <a href={item.link}>{item.name}</a>
            ) : (
              <span>{item.name}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
